import { useCallback, useState } from 'react';

// Tick boxes for a list, shared by every screen that has them.
//
// Written once because every list in the app needs the same thing — pick a few
// rows, do one thing to all of them — and because each screen that grew its own
// copy grew the same fault with it: the picked ids outlived the filter. Select
// five rows, change the status filter, press Delete, and rows nobody could see
// went with them.
//
// `chosen` is therefore the intersection of what is picked and what the caller
// is currently showing, and it is what a bulk action must act on. A row that
// scrolls out of the filter is dropped from the action, not silently included.
// The picked set itself is kept, so putting the filter back puts the ticks back.

export default function useSelection(visibleIds) {
  const [picked, setPicked] = useState(() => new Set());

  const all = (visibleIds || []).filter(Boolean);
  const chosen = all.filter((id) => picked.has(id));
  const allPicked = all.length > 0 && chosen.length === all.length;

  const toggle = useCallback((id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const clear = useCallback(() => setPicked(new Set()), []);

  // Replaces the selection outright, for a header box that means something
  // narrower than "everything" — "every unpaid invoice", say.
  const pickOnly = useCallback(
    (ids) => setPicked(new Set((ids || []).filter(Boolean))), []);

  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(all));

  return {
    chosen,                       // the ids a bulk action should act on
    count: chosen.length,
    allPicked,
    has: (id) => picked.has(id),
    toggle,
    toggleAll,
    pickOnly,
    clear,
  };
}
